import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { lazy, Suspense } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { MainLayout } from "@/components/layout/MainLayout";
import { Loader2 } from "lucide-react";
import ErrorBoundary from "@/components/ErrorBoundary";
import RouteErrorElement from "@/components/RouteErrorElement";
import { useAuthStore } from "@/features/auth/store/authStore";
import { PermissionGuard } from "@/components/layout/PermissionGuard";
import { PERMISSIONS, type Permission } from "@/lib/permissions";
const UnauthorizedPage = lazy(() => import("@/features/auth/UnauthorizedPage"));

const UsersPage = lazy(() => import("@/features/users/index"));
const RolesPage = lazy(() => import("@/features/roles/index"));
const RoleEditPage = lazy(() => import("@/features/roles/pages/EditRolePage"));
const UserDetailPage = lazy(
  () => import("@/features/users/pages/UserDetailPage"),
);
const UserEditPage = lazy(() => import("@/features/users/pages/UserEditPage"));
const ComponentShowcasePage = lazy(
  () => import("@/features/component-showcase"),
);
const LoginPage = lazy(() => import("@/features/auth/LoginPage"));
const ForgotPasswordPage = lazy(
  () => import("@/features/auth/ForgotPasswordPage"),
);
const SetupPasswordPage = lazy(
  () => import("@/features/auth/SetupPasswordPage"),
);
const VerificationExpiredPage = lazy(
  () => import("@/features/auth/VerificationExpiredPage"),
);
const CompaniesPage = lazy(() => import("@/features/companies"));
const CompanyEditPage = lazy(
  () => import("@/features/companies/pages/EditCompanyPage"),
);
const ClientsPage = lazy(() => import("@/features/clients"));
const EditClientPage = lazy(() =>
  import("@/features/clients").then((module) => ({
    default: module.EditClientPage,
  })),
);
const UnitsPage = lazy(() => import("@/features/units/index"));
const UnitDetailPage = lazy(
  () => import("@/features/units/pages/UnitDetailPage"),
);
const EditUnitPage = lazy(() => import("@/features/units/pages/EditUnitPage"));
const ProgramDetailsPage = lazy(() => import("@/features/programs/index"));
const ProgramsPage = lazy(
  () => import("@/features/programs/pages/ProgramsPage"),
);
const PondTrialsPage = lazy(() => import("@/features/pond-trials/index"));
const PLTrialsPage = lazy(() => import("@/features/pl-trial/index"));
const EditPLTrialPage = lazy(() =>
  import("@/features/pl-trial/index").then((module) => ({
    default: module.EditPLTrialPage,
  })),
);
const PLTrialDetailPage = lazy(() =>
  import("@/features/pl-trial/index").then((module) => ({
    default: module.PLTrialDetailPage,
  })),
);
const PLTrialSamplingListPage = lazy(() =>
  import("@/features/pl-trial/index").then((module) => ({
    default: module.PLTrialSamplingListPage,
  })),
);
const PLTrialPartialHarvestListPage = lazy(() =>
  import("@/features/pl-trial/index").then((module) => ({
    default: module.PLTrialPartialHarvestListPage,
  })),
);
const PLTrialHarvestDetailPage = lazy(() =>
  import("@/features/pl-trial/index").then((module) => ({
    default: module.PLTrialHarvestDetailPage,
  })),
);
const CageTrialsPage = lazy(() => import("@/features/cage-trial/index"));
const DiseaseChallengesPage = lazy(
  () => import("@/features/disease-challenges/index"),
);
const EditDiseaseChallengePage = lazy(() =>
  import("@/features/disease-challenges/index").then((module) => ({
    default: module.EditDiseaseChallengePage,
  })),
);
const DiseaseChallengeDetailPage = lazy(() =>
  import("@/features/disease-challenges/index").then((module) => ({
    default: module.DiseaseChallengeDetailPage,
  })),
);
const DiseaseChallengeWeightStockingDetailPage = lazy(() =>
  import("@/features/disease-challenges/index").then((module) => ({
    default: module.DiseaseChallengeWeightStockingDetailPage,
  })),
);
const DiseaseChallengeMonitoringDetailPage = lazy(() =>
  import("@/features/disease-challenges/index").then((module) => ({
    default: module.DiseaseChallengeMonitoringDetailPage,
  })),
);
const DiseaseChallengeMortalityListPage = lazy(() =>
  import("@/features/disease-challenges/index").then((module) => ({
    default: module.DiseaseChallengeMortalityListPage,
  })),
);
const DiseaseChallengeHarvestDetailPage = lazy(() =>
  import("@/features/disease-challenges/index").then((module) => ({
    default: module.DiseaseChallengeHarvestDetailPage,
  })),
);
const EditPondTrialPage = lazy(() =>
  import("@/features/pond-trials/index").then((module) => ({
    default: module.EditPondTrialPage,
  })),
);
const PondTrialDetailPage = lazy(() =>
  import("@/features/pond-trials/index").then((module) => ({
    default: module.PondTrialDetailPage,
  })),
);
const PondTrialWeightStockingDetailPage = lazy(() =>
  import("@/features/pond-trials/index").then((module) => ({
    default: module.PondTrialWeightStockingDetailPage,
  })),
);
const PondTrialMonitoringDetailPage = lazy(() =>
  import("@/features/pond-trials/index").then((module) => ({
    default: module.PondTrialMonitoringDetailPage,
  })),
);
const PondTrialMortalityListPage = lazy(() =>
  import("@/features/pond-trials/index").then((module) => ({
    default: module.PondTrialMortalityListPage,
  })),
);
const PondTrialHarvestDetailPage = lazy(() =>
  import("@/features/pond-trials/index").then((module) => ({
    default: module.PondTrialHarvestDetailPage,
  })),
);
const PondTrialHarvestCageDetailPage = lazy(() =>
  import("@/features/pond-trials/index").then((module) => ({
    default: module.PondTrialHarvestCageDetailPage,
  })),
);
const EditCageTrialPage = lazy(() =>
  import("@/features/cage-trial/index").then((module) => ({
    default: module.EditCageTrialPage,
  })),
);
const CageTrialDetailPage = lazy(() =>
  import("@/features/cage-trial/index").then((module) => ({
    default: module.CageTrialDetailPage,
  })),
);
const CageTrialWeightStockingDetailPage = lazy(() =>
  import("@/features/cage-trial/index").then((module) => ({
    default: module.CageTrialWeightStockingDetailPage,
  })),
);
const CageTrialMonitoringDetailPage = lazy(() =>
  import("@/features/cage-trial/index").then((module) => ({
    default: module.CageTrialMonitoringDetailPage,
  })),
);
const CageTrialMortalityListPage = lazy(() =>
  import("@/features/cage-trial/index").then((module) => ({
    default: module.CageTrialMortalityListPage,
  })),
);
const CageTrialHarvestDetailPage = lazy(() =>
  import("@/features/cage-trial/index").then((module) => ({
    default: module.CageTrialHarvestDetailPage,
  })),
);
const CageTrialHarvestCageDetailPage = lazy(() =>
  import("@/features/cage-trial/index").then((module) => ({
    default: module.CageTrialHarvestCageDetailPage,
  })),
);
const PitTagsPage = lazy(() => import("@/features/pit-tags/index"));
const PitTagDetailsPage = lazy(
  () => import("@/features/pit-tags/pages/PitTagDetailsPage"),
);
const SelectAvailableBroodstockPage = lazy(() =>
  import("@/features/programs/index").then((module) => ({
    default: module.SelectAvailableBroodstockPage,
  })),
);
const FacilitiesPage = lazy(() => import("@/features/facilities/index"));
const UserProfilePage = lazy(() => import("@/features/profile/index"));
const MatingHistoryPage = lazy(
  () => import("@/features/maturation/pages/MatingHistoryPage"),
);
const MatingFrequencyPage = lazy(
  () => import("@/features/maturation/pages/MatingFrequencyPage"),
);
const TotalMatingPage = lazy(
  () => import("@/features/maturation/pages/TotalMatingPage"),
);
const TotalAndAverageMatingPerTankPage = lazy(
  () => import("@/features/maturation/pages/TotalAndAverageMatingPerTankPage"),
);
const MortalityEntryPage = lazy(
  () => import("@/features/maturation/pages/MortalityEntryPage"),
);
const MortalityListPage = lazy(
  () => import("@/features/maturation/pages/MortalityListPage"),
);
const AnonymousMortalityListPage = lazy(
  () => import("@/features/maturation/pages/AnonymousMortalityListPage"),
);
const FeedingEntryPage = lazy(
  () => import("@/features/maturation/pages/FeedingEntryPage"),
);
const FeedingEntryDetailPage = lazy(
  () => import("@/features/maturation/pages/FeedingEntryDetailPage"),
);
const WeightBiomassPage = lazy(
  () => import("@/features/maturation/pages/WeightBiomassPage"),
);
const WeightBiomassDetailPage = lazy(
  () => import("@/features/maturation/pages/WeightBiomassDetailPage"),
);
const WeightBiomassObservationPage = lazy(
  () => import("@/features/maturation/pages/WeightBiomassObservationPage"),
);
const WeightBiomassAddObservationPage = lazy(
  () => import("@/features/maturation/pages/WeightBiomassAddObservationPage"),
);
const UpdateBroodstockPage = lazy(
  () => import("@/features/programs/pages/UpdateBroodstockPage"),
);
const UpdateEliteBroodstockPage = lazy(
  () => import("@/features/programs/pages/UpdateEliteBroodstockPage"),
);
const UpdatePhenotypingPage = lazy(
  () => import("@/features/programs/pages/UpdatePhenotypingPage"),
);
const UpdateElitePhenotypingPage = lazy(
  () => import("@/features/programs/pages/UpdateElitePhenotypingPage"),
);
const BatchesPage = lazy(() => import("../features/batches/pages/BatchesPage"));
const BatchesDetailPage = lazy(
  () => import("@/features/batches/pages/BatchesDetailPage"),
);
const AddPreHatchSelectionPage = lazy(
  () => import("@/features/batches/pages/AddPreHatchSelectionPage"),
);
const HatchingDetailPage = lazy(
  () => import("@/features/batches/pages/HatchingDetailPage"),
);
const AddHatchingPage = lazy(
  () => import("@/features/batches/pages/AddHatchingPage"),
);
const AvgPL10PopulationPage = lazy(
  () => import("@/features/batches/pages/AvgPL10PopulationPage"),
);
const AvgEggAndNaupliiPage = lazy(
  () => import("@/features/batches/pages/AvgEggAndNaupliiPage"),
);
const AvgFertilAndHatchingPage = lazy(
  () => import("@/features/batches/pages/AvgFertilAndHatchingPage"),
);
const LarvaeTankTemperaturePage = lazy(
  () => import("@/features/batches/pages/LarvaeTankTemperaturePage"),
);
const PL4PL10PopulationPage = lazy(
  () => import("@/features/batches/pages/PL4PL10PopulationPage"),
);
const PLTrialBatchPage = lazy(
  () => import("@/features/batches/pages/PLTrialBatchPage"),
);
const HarvestPage = lazy(() => import("@/features/batches/pages/HarvestPage"));
const UpdateEggsFertilPage = lazy(
  () => import("@/features/batches/pages/UpdateEggsFertilPage"),
);
const StockToLarvaePage = lazy(
  () => import("@/features/batches/pages/StockToLarvaePage"),
);
const LarvaeDetailPage = lazy(
  () => import("@/features/batches/pages/LarvaeDetailPage"),
);
const AddLarvaeMonitoringPage = lazy(
  () => import("@/features/batches/pages/AddLarvaeMonitoringPage"),
);
const AddLarvaeTemperaturePage = lazy(
  () => import("@/features/batches/pages/AddLarvaeTemperaturePage"),
);
const GrowoutWeightSamplingPage = lazy(
  () => import("@/features/batches/pages/GrowoutWeightSamplingPage"),
);
const Add3GrSelectionPage = lazy(
  () => import("@/features/batches/pages/Add3GrSelectionPage"),
);
const Add50RecordsSamplingPage = lazy(
  () => import("@/features/batches/pages/Add50RecordsSamplingPage"),
);
const Add20GrSelectionPage = lazy(
  () => import("@/features/batches/pages/Add20GrSelectionPage"),
);
const Update20GrSelectionPage = lazy(
  () => import("@/features/batches/pages/Update20GrSelectionPage"),
);
const PercentageMatingMortalityPage = lazy(
  () => import("@/features/maturation/pages/PercentageMatingMortalityPage"),
);
const DailyFeedingPage = lazy(
  () => import("@/features/maturation/pages/DailyFeedingPage"),
);
const MatingPerTankBreakdownPage = lazy(
  () => import("@/features/maturation/pages/MatingPerTankBreakdownPage"),
);
const AvgWeeklyGrowRateBatchPage = lazy(
  () => import("@/features/growout/pages/AvgWeeklyGrowRateBatchPage"),
);
const AvgDailyGainBatchPage = lazy(
  () => import("@/features/growout/pages/AvgDailyGainBatchPage"),
);
const Add20GrSamplingPage = lazy(
  () => import("@/features/batches/pages/Add20GrSamplingPage"),
);
const OtherGenotypingPage = lazy(
  () => import("@/features/batches/pages/OtherGenotypingPage"),
);
const StockToGoPage = lazy(
  () => import("@/features/batches/pages/StockToGoPage"),
);
const GrowoutPage = lazy(() => import("@/features/growout/pages/GrowoutPage"));
const GrowoutDetailPage = lazy(
  () => import("@/features/growout/pages/GrowoutDetailPage"),
);
const GrowoutTagsPage = lazy(
  () => import("@/features/growout/pages/GrowoutTagsPage"),
);
const GeneticBroodstockPage = lazy(
  () => import("@/features/genetic-broodstock"),
);
const GeneticBroodstockDetailsPage = lazy(
  () =>
    import("@/features/genetic-broodstock/pages/GeneticBroodstockDetailsPage"),
);
const GeneticBroodstockViewTagsPage = lazy(
  () =>
    import("@/features/genetic-broodstock/pages/GeneticBroodstockViewTagsPage"),
);
const GeneticBroodstockWeightSamplingPage = lazy(
  () =>
    import(
      "@/features/genetic-broodstock/pages/GeneticBroodstockWeightSamplingPage"
    ),
);

const MyTasksPage = lazy(() => import("@/features/my-tasks/index"));
const TaskDetailPage = lazy(
  () => import("@/features/my-tasks/pages/TaskDetailPage"),
);

const ScheduleTemplatePage = lazy(() => import("@/features/schedule-template"));
const ScheduleTemplateDetail = lazy(
  () => import("@/features/schedule-template/pages/ScheduleTemplateDetail"),
);

const ScheduleGroupTasksPage = lazy(
  () => import("@/features/schedule-group-tasks"),
);
const ScheduleGroupTaskDetail = lazy(
  () => import("@/features/schedule-group-tasks/pages/ScheduleGroupTaskDetail"),
);

const RedelegateTasksPage = lazy(
  () => import("@/features/redelegate-tasks/index"),
);
const AddRedelegateTaskPage = lazy(() =>
  import("@/features/redelegate-tasks/index").then((module) => ({
    default: module.AddRedelegateTaskPage,
  })),
);

const ReportWeightSRPage = lazy(
  () => import("@/features/pond-trials/pages/ReportWeightSRPage"),
);
const ReportCageDeformitiesPage = lazy(
  () => import("@/features/pond-trials/pages/ReportCageDeformitiesPage"),
);
const ReportAllCagesDeformitiesPage = lazy(
  () => import("@/features/pond-trials/pages/ReportAllCagesDeformitiesPage"),
);
const ReportNbcDeformitiesPage = lazy(
  () => import("@/features/pond-trials/pages/ReportNbcDeformitiesPage"),
);
const ReportTaggingShippingPage = lazy(
  () => import("@/features/pond-trials/pages/ReportTaggingShippingPage"),
);
const CageTrialReportWeightSRPage = lazy(
  () => import("@/features/cage-trial/pages/ReportWeightSRPage"),
);
const CageTrialReportCageDeformitiesPage = lazy(
  () => import("@/features/cage-trial/pages/ReportCageDeformitiesPage"),
);
const CageTrialReportAllCagesDeformitiesPage = lazy(
  () => import("@/features/cage-trial/pages/ReportAllCagesDeformitiesPage"),
);
const CageTrialReportNbcDeformitiesPage = lazy(
  () => import("@/features/cage-trial/pages/ReportNbcDeformitiesPage"),
);
const CageTrialReportTaggingShippingPage = lazy(
  () => import("@/features/cage-trial/pages/ReportTaggingShippingPage"),
);

const DiseaseChallengeReportWeightSRPage = lazy(
  () => import("@/features/disease-challenges/pages/ReportWeightSRPage"),
);
const DiseaseChallengeReportCageDeformitiesPage = lazy(
  () => import("@/features/disease-challenges/pages/ReportCageDeformitiesPage"),
);
const DiseaseChallengeReportAllCagesDeformitiesPage = lazy(
  () =>
    import("@/features/disease-challenges/pages/ReportAllCagesDeformitiesPage"),
);
const PLTrialReportSampleSubmissionPage = lazy(
  () => import("@/features/pl-trial/pages/ReportSampleSubmissionPage"),
);
const CageTrialReportSampleSubmissionPage = lazy(
  () => import("@/features/cage-trial/pages/ReportSampleSubmissionPage"),
);
const DiseaseChallengeReportSampleSubmissionPage = lazy(
  () => import("@/features/disease-challenges/pages/ReportSampleSubmissionPage"),
);
const GrowoutReport20GrSampleSubmissionPage = lazy(
  () => import("@/features/batches/pages/Report20GrSampleSubmissionPage"),
);
const CyclesPage = lazy(
  () => import("@/features/production/pages/CyclesPage"),
);
const CycleDetailPage = lazy(
  () => import("@/features/production/pages/CycleDetailPage"),
);
const ReceivingTankMortalityPage = lazy(
  () => import("@/features/production/pages/ReceivingTankMortalityPage"),
);
const ReceivingTankFeedingPage = lazy(
  () => import("@/features/production/pages/ReceivingTankFeedingPage"),
);
const ReceivingTankAbwPage = lazy(
  () => import("@/features/production/pages/ReceivingTankAbwPage"),
);
const ReceivingTankTemperaturePage = lazy(
  () => import("@/features/production/pages/ReceivingTankTemperaturePage"),
);
const ReceivingTankWaterQualityPage = lazy(
  () => import("@/features/production/pages/ReceivingTankWaterQualityPage"),
);
const MaturationTankMortalityPage = lazy(
  () => import("@/features/production/pages/MaturationTankMortalityPage"),
);
const MaturationTankFeedingPage = lazy(
  () => import("@/features/production/pages/MaturationTankFeedingPage"),
);
const MaturationTankAbwPage = lazy(
  () => import("@/features/production/pages/MaturationTankAbwPage"),
);
const MaturationTankTemperaturePage = lazy(
  () => import("@/features/production/pages/MaturationTankTemperaturePage"),
);
const MaturationTankWaterQualityPage = lazy(
  () => import("@/features/production/pages/MaturationTankWaterQualityPage"),
);
const TankPopulationPage = lazy(
  () => import("@/features/production/pages/TankPopulationPage"),
);

const PageLoader = () => (
  <div className="flex flex-col justify-center items-center h-64 space-y-4">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
    <p className="text-sm text-neutral-600 font-medium">Loading page...</p>
  </div>
);

const PageWithErrorBoundary = ({ children }: { children: React.ReactNode }) => (
  <ErrorBoundary fullScreen={false}>{children}</ErrorBoundary>
);

const AuthGuard = ({ children }: { children: React.ReactNode }) => {
  const isAuthenticated = useAuthStore((state) => Boolean(state.accessToken));
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

const LoginRedirect = ({ children }: { children: React.ReactNode }) => {
  const isAuthenticated = useAuthStore((state) => Boolean(state.accessToken));
  const location = useLocation();

  if (isAuthenticated) {
    const from =
      (location.state as { from?: { pathname?: string } })?.from?.pathname ||
      "/my-tasks";
    return <Navigate to={from} replace />;
  }

  return <>{children}</>;
};

const PlaceholderPage = ({ title }: { title: string }) => (
  <div className="space-y-2">
    <h1 className="text-2xl font-semibold text-neutral-950">{title}</h1>
    <p className="text-neutral-600">This section is under construction.</p>
  </div>
);

const withGuard = (element: React.ReactNode, permissions: Permission[]) => (
  <PermissionGuard requiredPermissions={permissions}>{element}</PermissionGuard>
);

const router = createBrowserRouter([
  {
    path: "/login",
    element: (
      <PageWithErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <LoginRedirect>
            <LoginPage />
          </LoginRedirect>
        </Suspense>
      </PageWithErrorBoundary>
    ),
  },
  {
    path: "/forgot-password",
    element: (
      <PageWithErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <LoginRedirect>
            <ForgotPasswordPage />
          </LoginRedirect>
        </Suspense>
      </PageWithErrorBoundary>
    ),
  },
  {
    path: "/setup-password",
    element: (
      <PageWithErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <LoginRedirect>
            <SetupPasswordPage />
          </LoginRedirect>
        </Suspense>
      </PageWithErrorBoundary>
    ),
  },
  {
    path: "/verification-expired",
    element: (
      <PageWithErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <LoginRedirect>
            <VerificationExpiredPage />
          </LoginRedirect>
        </Suspense>
      </PageWithErrorBoundary>
    ),
  },
  {
    path: "/unauthorized",
    element: (
      <PageWithErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <UnauthorizedPage />
        </Suspense>
      </PageWithErrorBoundary>
    ),
  },
  {
    path: "/",
    element: (
      <PageWithErrorBoundary>
        <AuthGuard>
          <MainLayout />
        </AuthGuard>
      </PageWithErrorBoundary>
    ),
    children: [
      {
        index: true,
        element: <Navigate to="/my-tasks" replace />,
      },
      {
        path: "production/cycles",
        element: (
          <Suspense fallback={<PageLoader />}>
            <CyclesPage />
          </Suspense>
        ),
      },
      {
        path: "production/tank-population",
        element: (
          <Suspense fallback={<PageLoader />}>
            <TankPopulationPage />
          </Suspense>
        ),
      },
      {
        path: "production/tank-population/:tab",
        element: (
          <Suspense fallback={<PageLoader />}>
            <TankPopulationPage />
          </Suspense>
        ),
      },
      {
        path: "production/cycles/:cycleId/receiving-tank/mortality",
        element: (
          <Suspense fallback={<PageLoader />}>
            <ReceivingTankMortalityPage />
          </Suspense>
        ),
      },
      {
        path: "production/cycles/:cycleId/mortality",
        element: (
          <Suspense fallback={<PageLoader />}>
            <ReceivingTankMortalityPage />
          </Suspense>
        ),
      },
      {
        path: "production/cycles/:cycleId/receiving-tank/feeding",
        element: (
          <Suspense fallback={<PageLoader />}>
            <ReceivingTankFeedingPage />
          </Suspense>
        ),
      },
      {
        path: "production/cycles/:cycleId/feeding",
        element: (
          <Suspense fallback={<PageLoader />}>
            <ReceivingTankFeedingPage />
          </Suspense>
        ),
      },
      {
        path: "production/cycles/:cycleId/receiving-tank/abw",
        element: (
          <Suspense fallback={<PageLoader />}>
            <ReceivingTankAbwPage />
          </Suspense>
        ),
      },
      {
        path: "production/cycles/:cycleId/receiving-tank/temperature",
        element: (
          <Suspense fallback={<PageLoader />}>
            <ReceivingTankTemperaturePage />
          </Suspense>
        ),
      },
      {
        path: "production/cycles/:cycleId/receiving-tank/water-quality",
        element: (
          <Suspense fallback={<PageLoader />}>
            <ReceivingTankWaterQualityPage />
          </Suspense>
        ),
      },
      {
        path: "production/cycles/:cycleId/abw",
        element: (
          <Suspense fallback={<PageLoader />}>
            <ReceivingTankAbwPage />
          </Suspense>
        ),
      },
      {
        path: "production/cycles/:cycleId/maturation-tank/mortality",
        element: (
          <Suspense fallback={<PageLoader />}>
            <MaturationTankMortalityPage />
          </Suspense>
        ),
      },
      {
        path: "production/cycles/:cycleId/maturation-tank/feeding",
        element: (
          <Suspense fallback={<PageLoader />}>
            <MaturationTankFeedingPage />
          </Suspense>
        ),
      },
      {
        path: "production/cycles/:cycleId/maturation-tank/abw",
        element: (
          <Suspense fallback={<PageLoader />}>
            <MaturationTankAbwPage />
          </Suspense>
        ),
      },
      {
        path: "production/cycles/:cycleId/maturation-tank/temperature",
        element: (
          <Suspense fallback={<PageLoader />}>
            <MaturationTankTemperaturePage />
          </Suspense>
        ),
      },
      {
        path: "production/cycles/:cycleId/maturation-tank/water-quality",
        element: (
          <Suspense fallback={<PageLoader />}>
            <MaturationTankWaterQualityPage />
          </Suspense>
        ),
      },
      {
        path: "production/cycles/:cycleId",
        element: (
          <Suspense fallback={<PageLoader />}>
            <CycleDetailPage />
          </Suspense>
        ),
      },
      {
        path: "production/cycles/:cycleId/:tab",
        element: (
          <Suspense fallback={<PageLoader />}>
            <CycleDetailPage />
          </Suspense>
        ),
      },
      {
        path: "users",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <UsersPage />
          </Suspense>,
          [PERMISSIONS.USER_VIEW],
        ),
      },
      {
        path: "users/roles",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <RolesPage />
          </Suspense>,
          [PERMISSIONS.ROLES_VIEW],
        ),
      },
      {
        path: "users/roles/edit",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <RoleEditPage />
          </Suspense>,
          [PERMISSIONS.ROLES_VIEW],
        ),
      },
      {
        path: "users/roles/:roleId/edit",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <RoleEditPage />
          </Suspense>,
          [PERMISSIONS.ROLES_VIEW],
        ),
      },
      {
        path: "users/:userId",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <UserDetailPage />
          </Suspense>,
          [PERMISSIONS.USER_VIEW],
        ),
      },
      {
        path: "users/:userId/edit",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <UserEditPage />
          </Suspense>,
          [PERMISSIONS.USER_VIEW],
        ),
      },
      {
        path: "components",
        element: (
          <Suspense fallback={<PageLoader />}>
            <ComponentShowcasePage />
          </Suspense>
        ),
      },
      {
        path: "companies",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <CompaniesPage />
          </Suspense>,
          [PERMISSIONS.COMPANY_VIEW],
        ),
      },
      {
        path: "companies/:companyId/edit",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <CompanyEditPage />
          </Suspense>,
          [PERMISSIONS.COMPANY_VIEW],
        ),
      },
      {
        path: "companies/clients",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <ClientsPage />
          </Suspense>,
          [PERMISSIONS.CLIENT_VIEW],
        ),
      },
      {
        path: "companies/clients/:clientId/edit",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <EditClientPage />
          </Suspense>,
          [PERMISSIONS.CLIENT_VIEW],
        ),
      },
      {
        path: "companies/units",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <UnitsPage />
          </Suspense>,
          [PERMISSIONS.UNIT_VIEW],
        ),
      },
      {
        path: "companies/units/:unitId",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <UnitDetailPage />
          </Suspense>,
          [PERMISSIONS.UNIT_VIEW],
        ),
      },
      {
        path: "companies/units/:unitId/edit",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <EditUnitPage />
          </Suspense>,
          [PERMISSIONS.UNIT_VIEW],
        ),
      },
      {
        path: "companies/pit-tags",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <PitTagsPage />
          </Suspense>,
          [PERMISSIONS.TAG_VIEW],
        ),
      },
      {
        path: "companies/pit-tags/:id",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <PitTagDetailsPage />
          </Suspense>,
          [PERMISSIONS.TAG_VIEW],
        ),
      },
      {
        path: "companies/facilities",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <FacilitiesPage />
          </Suspense>,
          [PERMISSIONS.FACILITY_VIEW],
        ),
      },
      {
        path: "programs",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <ProgramsPage />
          </Suspense>,
          [PERMISSIONS.BREEDPROGRAM_VIEW],
        ),
      },
      {
        path: "pond-trials",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <PondTrialsPage />
          </Suspense>,
          [PERMISSIONS.BREEDPROGRAM_VIEW],
        ),
      },
      {
        path: "pl-trial",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <PLTrialsPage />
          </Suspense>,
          [PERMISSIONS.CAGETRIAL_VIEW],
        ),
      },
      {
        path: "pl-trial/:trialId/edit",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <EditPLTrialPage />
          </Suspense>,
          [PERMISSIONS.CAGETRIAL_UPDATE],
        ),
      },
      {
        path: "pl-trial/:trialId/sampling/:locationId",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <PLTrialSamplingListPage />
          </Suspense>,
          [PERMISSIONS.CAGETRIAL_VIEW],
        ),
      },
      {
        path: "pl-trial/:trialId/partial-harvest/:locationId",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <PLTrialPartialHarvestListPage />
          </Suspense>,
          [PERMISSIONS.CAGETRIAL_VIEW],
        ),
      },
      {
        path: "pl-trial/:trialId/harvest/:locationId",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <PLTrialHarvestDetailPage />
          </Suspense>,
          [PERMISSIONS.CAGETRIAL_VIEW],
        ),
      },
      {
        path: "pl-trial/:trialId/:tab?",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <PLTrialDetailPage />
          </Suspense>,
          [PERMISSIONS.CAGETRIAL_VIEW],
        ),
      },
      {
        path: "cage-trial",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <CageTrialsPage />
          </Suspense>,
          [PERMISSIONS.CAGETRIAL_VIEW],
        ),
      },
      {
        path: "cage-trial/:trialId/edit",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <EditCageTrialPage />
          </Suspense>,
          [PERMISSIONS.CAGETRIAL_UPDATE],
        ),
      },
      {
        path: "cage-trial/:trialId/:tab?",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <CageTrialDetailPage />
          </Suspense>,
          [PERMISSIONS.CAGETRIAL_VIEW],
        ),
      },
      {
        path: "cage-trial/:trialId/weight-stocking/:locationAliasId",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <CageTrialWeightStockingDetailPage />
          </Suspense>,
          [PERMISSIONS.CAGETRIAL_VIEW],
        ),
      },
      {
        path: "cage-trial/:trialId/monitoring/:monitoringId",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <CageTrialMonitoringDetailPage />
          </Suspense>,
          [PERMISSIONS.CAGETRIAL_VIEW],
        ),
      },
      {
        path: "cage-trial/:trialId/mortality/list",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <CageTrialMortalityListPage />
          </Suspense>,
          [PERMISSIONS.CAGETRIAL_VIEW],
        ),
      },
      {
        path: "cage-trial/:trialId/harvest/:locationId",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <CageTrialHarvestDetailPage />
          </Suspense>,
          [PERMISSIONS.CAGETRIAL_VIEW],
        ),
      },
      {
        path: "cage-trial/:trialId/harvest/:locationId/cage/:cage/record/:recordId",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <CageTrialHarvestCageDetailPage />
          </Suspense>,
          [PERMISSIONS.CAGETRIAL_VIEW],
        ),
      },
      {
        path: "pond-trials/:trialId/edit",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <EditPondTrialPage />
          </Suspense>,
          [PERMISSIONS.BREEDPROGRAM_VIEW],
        ),
      },
      {
        path: "pond-trials/:trialId/:tab?",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <PondTrialDetailPage />
          </Suspense>,
          [PERMISSIONS.BREEDPROGRAM_VIEW],
        ),
      },
      {
        path: "pond-trials/:trialId/weight-stocking/:locationAliasId",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <PondTrialWeightStockingDetailPage />
          </Suspense>,
          [PERMISSIONS.BREEDPROGRAM_VIEW],
        ),
      },
      {
        path: "pond-trials/:trialId/monitoring/:monitoringId",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <PondTrialMonitoringDetailPage />
          </Suspense>,
          [PERMISSIONS.BREEDPROGRAM_VIEW],
        ),
      },
      {
        path: "pond-trials/:trialId/mortality/list",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <PondTrialMortalityListPage />
          </Suspense>,
          [PERMISSIONS.BREEDPROGRAM_VIEW],
        ),
      },
      {
        path: "pond-trials/:trialId/harvest/:locationId",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <PondTrialHarvestDetailPage />
          </Suspense>,
          [PERMISSIONS.BREEDPROGRAM_VIEW],
        ),
      },
      {
        path: "pond-trials/:trialId/harvest/:locationId/cage/:cage/record/:recordId",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <PondTrialHarvestCageDetailPage />
          </Suspense>,
          [PERMISSIONS.BREEDPROGRAM_VIEW],
        ),
      },
      {
        path: "disease-challenges",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <DiseaseChallengesPage />
          </Suspense>,
          [PERMISSIONS.DISEASECHALLENGE_VIEW],
        ),
      },
      {
        path: "disease-challenges/:id/edit",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <EditDiseaseChallengePage />
          </Suspense>,
          [PERMISSIONS.DISEASECHALLENGE_UPDATE],
        ),
      },
      {
        path: "disease-challenges/:id/:tab?",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <DiseaseChallengeDetailPage />
          </Suspense>,
          [PERMISSIONS.DISEASECHALLENGE_VIEW],
        ),
      },
      {
        path: "disease-challenges/:id/weight-stocking/:tankId",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <DiseaseChallengeWeightStockingDetailPage />
          </Suspense>,
          [PERMISSIONS.DISEASECHALLENGE_VIEW],
        ),
      },
      {
        path: "disease-challenges/:id/monitoring/:tankId",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <DiseaseChallengeMonitoringDetailPage />
          </Suspense>,
          [PERMISSIONS.DISEASECHALLENGE_VIEW],
        ),
      },
      {
        path: "disease-challenges/:id/mortality/list",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <DiseaseChallengeMortalityListPage />
          </Suspense>,
          [PERMISSIONS.DISEASECHALLENGE_VIEW],
        ),
      },
      {
        path: "disease-challenges/:id/harvest/:harvestRowId",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <DiseaseChallengeHarvestDetailPage />
          </Suspense>,
          [PERMISSIONS.DISEASECHALLENGE_VIEW],
        ),
      },
      {
        path: "genetic-broodstock",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <GeneticBroodstockPage />
          </Suspense>,
          [PERMISSIONS.BREEDGENTANK_VIEW],
        ),
      },
      {
        path: "genetic-broodstock/:id",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <GeneticBroodstockDetailsPage />
          </Suspense>,
          [PERMISSIONS.BREEDGENTANK_VIEW],
        ),
      },
      {
        path: "genetic-broodstock/:id/tags",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <GeneticBroodstockViewTagsPage />
          </Suspense>,
          [PERMISSIONS.BREEDGENTANK_VIEW],
        ),
      },
      {
        path: "genetic-broodstock/:id/weight-sampling",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <GeneticBroodstockWeightSamplingPage />
          </Suspense>,
          [PERMISSIONS.BREEDGENTANK_VIEW],
        ),
      },
      {
        path: "genetic-broodstock/weight-sampling",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <GeneticBroodstockWeightSamplingPage />
          </Suspense>,
          [PERMISSIONS.BREEDGENTANK_VIEW],
        ),
      },
      {
        path: "programs/:programId",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <ProgramDetailsPage />
          </Suspense>,
          [PERMISSIONS.BREEDPROGRAM_VIEW],
        ),
      },
      {
        path: "programs/:programId/select-broodstock",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <SelectAvailableBroodstockPage />
          </Suspense>,
          [PERMISSIONS.BREEDPROGRAM_VIEW],
        ),
      },
      {
        path: "batches",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <BatchesPage />
          </Suspense>,
          [PERMISSIONS.BREEDBATCH_VIEW],
        ),
      },
      {
        path: "batches/:batchId",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <BatchesDetailPage />
          </Suspense>,
          [PERMISSIONS.BREEDBATCH_VIEW],
        ),
      },
      {
        path: "batches/:batchId/pre-hatch",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <BatchesDetailPage />
          </Suspense>,
          [PERMISSIONS.BREEDBATCH_VIEW],
        ),
      },
      {
        path: "batches/:batchId/hatching",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <BatchesDetailPage />
          </Suspense>,
          [PERMISSIONS.BREEDBATCH_VIEW],
        ),
      },
      {
        path: "batches/:batchId/larvae",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <BatchesDetailPage />
          </Suspense>,
          [PERMISSIONS.BREEDBATCH_VIEW],
        ),
      },
      {
        path: "batches/:batchId/growout/:tab?",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <BatchesDetailPage />
          </Suspense>,
          [PERMISSIONS.BREEDBATCH_VIEW],
        ),
      },
      {
        path: "batches/:batchId/add-pre-hatch",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <AddPreHatchSelectionPage />
          </Suspense>,
          [PERMISSIONS.BREEDBATCH_VIEW],
        ),
      },
      {
        path: "batches/:batchId/add-hatching",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <AddHatchingPage />
          </Suspense>,
          [PERMISSIONS.BREEDHATCHING_ADDHATCHING],
        ),
      },
      {
        path: "batches/:batchId/hatching/:id",
        element: withGuard(<HatchingDetailPage />, [
          PERMISSIONS.BREEDHATCHING_VIEW,
        ]),
      },
      {
        path: "batches/:batchId/hatching/:id/eggs-fertil",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <UpdateEggsFertilPage />
          </Suspense>,
          [PERMISSIONS.BREEDHATCHING_HARVEST],
        ),
      },
      {
        path: "batches/:batchId/hatching/:id/update-eggs-fertil",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <UpdateEggsFertilPage />
          </Suspense>,
          [PERMISSIONS.BREEDHATCHING_HARVEST],
        ),
      },
      {
        path: "batches/:batchId/hatching/:id/harvest",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <HarvestPage />
          </Suspense>,
          [PERMISSIONS.BREEDHATCHING_HARVEST],
        ),
      },
      {
        path: "batches/:batchId/hatching/:id/stock-larvae",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <StockToLarvaePage />
          </Suspense>,
          [PERMISSIONS.BREEDHATCHING_STOCKTOLARVAE],
        ),
      },
      {
        path: "batches/:batchId/larvae/:id/:tab?",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <LarvaeDetailPage />
          </Suspense>,
          [PERMISSIONS.BREEDBATCH_VIEW],
        ),
      },
      {
        path: "batches/:batchId/larvae/:id/monitoring/add",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <AddLarvaeMonitoringPage />
          </Suspense>,
          [PERMISSIONS.BREEDLARVAE_ADDMONITORING],
        ),
      },
      {
        path: "batches/:batchId/larvae/:id/monitoring/:monitoringId/edit",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <AddLarvaeMonitoringPage />
          </Suspense>,
          [PERMISSIONS.BREEDLARVAE_ADDMONITORING],
        ),
      },
      {
        path: "batches/:batchId/larvae/:id/pl4-monitoring/add",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <AddLarvaeMonitoringPage />
          </Suspense>,
          [PERMISSIONS.BREEDLARVAE_ADDMONITORING_PL4],
        ),
      },
      {
        path: "batches/:batchId/larvae/:id/pl4-monitoring/:monitoringId/edit",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <AddLarvaeMonitoringPage />
          </Suspense>,
          [PERMISSIONS.BREEDLARVAE_ADDMONITORING_PL4],
        ),
      },
      {
        path: "batches/:batchId/larvae/:id/temperature/add",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <AddLarvaeTemperaturePage />
          </Suspense>,
          [PERMISSIONS.BREEDLARVAE_ADDTEMPERATURE],
        ),
      },
      {
        path: "batches/:batchId/larvae/:id/temperature/:temperatureId/edit",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <AddLarvaeTemperaturePage />
          </Suspense>,
          [PERMISSIONS.BREEDLARVAE_ADDTEMPERATURE],
        ),
      },
      {
        path: "batches/:batchId/larvae/:id/stock-to-go",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <StockToGoPage />
          </Suspense>,
          [PERMISSIONS.BREEDBATCH_VIEW],
        ),
      },
      {
        path: "batches/:batchId/growout/:id/weight-sampling",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <GrowoutWeightSamplingPage />
          </Suspense>,
          [PERMISSIONS.BREEDBATCH_VIEW],
        ),
      },
      {
        path: "batches/:batchId/growout/:id/3gr-selection",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <Add3GrSelectionPage />
          </Suspense>,
          [PERMISSIONS.BREEDBATCH_VIEW],
        ),
      },
      {
        path: "batches/:batchId/growout/:id/50-records-sampling",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <Add50RecordsSamplingPage />
          </Suspense>,
          [PERMISSIONS.BREEDBATCH_VIEW],
        ),
      },
      {
        path: "batches/:batchId/growout/:id/20gr-selection",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <Add20GrSelectionPage />
          </Suspense>,
          [PERMISSIONS.BREEDBATCH_VIEW],
        ),
      },
      {
        path: "batches/:batchId/growout/:id/update-20gr-selection",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <Update20GrSelectionPage />
          </Suspense>,
          [PERMISSIONS.BREEDBATCH_VIEW],
        ),
      },
      {
        path: "batches/:batchId/growout/:id/20gr-selection/update",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <Update20GrSelectionPage />
          </Suspense>,
          [PERMISSIONS.BREEDBATCH_VIEW],
        ),
      },
      {
        path: "batches/:batchId/growout/:id/20gr-sampling",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <Add20GrSamplingPage />
          </Suspense>,
          [PERMISSIONS.BREEDBATCH_VIEW],
        ),
      },
      {
        path: "batches/:batchId/growout/:id/other-genotyping",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <OtherGenotypingPage />
          </Suspense>,
          [PERMISSIONS.BREEDBATCH_VIEW],
        ),
      },
      {
        path: "maturation/mating-frequency",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <MatingFrequencyPage />
          </Suspense>,
          [PERMISSIONS.BREEDMATING_VIEW],
        ),
      },
      {
        path: "maturation/mating-frequency/history",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <MatingHistoryPage />
          </Suspense>,
          [PERMISSIONS.BREEDMATING_VIEW],
        ),
      },
      {
        path: "maturation/mating-frequency/:id/total-mating",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <TotalMatingPage />
          </Suspense>,
          [PERMISSIONS.BREEDMATING_VIEW],
        ),
      },
      {
        path: "programs/:programId/maturation-tanks/:tankId/update-broodstock",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <UpdateBroodstockPage />
          </Suspense>,
          [PERMISSIONS.BREEDPROGRAM_VIEW],
        ),
      },
      {
        path: "programs/:programId/maturation-elite-tanks/:tankId/update-elite-broodstock",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <UpdateEliteBroodstockPage />
          </Suspense>,
          [PERMISSIONS.BREEDPROGRAM_VIEW],
        ),
      },
      {
        path: "programs/:programId/maturation-tanks/:unitId/update-phenotyping",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <UpdatePhenotypingPage />
          </Suspense>,
          [PERMISSIONS.BREEDPROGRAM_VIEW],
        ),
      },
      {
        path: "programs/:programId/maturation-elite-tanks/:unitId/update-phenotyping",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <UpdateElitePhenotypingPage />
          </Suspense>,
          [PERMISSIONS.BREEDPROGRAM_VIEW],
        ),
      },
      {
        path: "maturation/mortality-entry",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <MortalityEntryPage />
          </Suspense>,
          [PERMISSIONS.BREEDMATING_VIEW],
        ),
      },
      {
        path: "growout",
        children: [
          {
            element: withGuard(
              <Suspense fallback={<PageLoader />}>
                <GrowoutPage />
              </Suspense>,
              [PERMISSIONS.BREEDGO_VIEW],
            ),
          },
          {
            path: ":id",
            element: withGuard(
              <Suspense fallback={<PageLoader />}>
                <GrowoutDetailPage />
              </Suspense>,
              [PERMISSIONS.BREEDGO_VIEW],
            ),
          },
          {
            path: ":id/tags",
            element: withGuard(
              <Suspense fallback={<PageLoader />}>
                <GrowoutTagsPage />
              </Suspense>,
              [PERMISSIONS.BREEDGO_VIEW],
            ),
          },
        ],
      },
      {
        path: "maturation/mortality-entry/list",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <MortalityListPage />
          </Suspense>,
          [PERMISSIONS.BREEDMATING_VIEW],
        ),
      },
      {
        path: "maturation/mortality-entry/anonymous",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <AnonymousMortalityListPage />
          </Suspense>,
          [PERMISSIONS.BREEDMATING_VIEW],
        ),
      },
      {
        path: "maturation/feeding-entry",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <FeedingEntryPage />
          </Suspense>,
          [PERMISSIONS.BREEDMATING_VIEW],
        ),
      },
      {
        path: "maturation/feeding-entry/:id",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <FeedingEntryDetailPage />
          </Suspense>,
          [PERMISSIONS.BREEDMATING_VIEW],
        ),
      },
      {
        path: "maturation/weight-biomass-entry",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <WeightBiomassPage />
          </Suspense>,
          [PERMISSIONS.BREEDMATING_VIEW],
        ),
      },
      {
        path: "maturation/weight-biomass-entry/:id",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <WeightBiomassDetailPage />
          </Suspense>,
          [PERMISSIONS.BREEDMATING_VIEW],
        ),
      },
      {
        path: "maturation/weight-biomass-entry/:id/observation/:observationId",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <WeightBiomassObservationPage />
          </Suspense>,
          [PERMISSIONS.BREEDMATING_VIEW],
        ),
      },
      {
        path: "maturation/weight-biomass-entry/:id/add-observation",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <WeightBiomassAddObservationPage />
          </Suspense>,
          [PERMISSIONS.BREEDMATING_VIEW],
        ),
      },
      {
        path: "settings",
        element: (
          <Suspense fallback={<PageLoader />}>
            <UserProfilePage />
          </Suspense>
        ),
      },
      {
        path: "analytics",
        element: (
          <PageWithErrorBoundary>
            <PlaceholderPage title="Analytics" />
          </PageWithErrorBoundary>
        ),
      },
      {
        path: "reports/maturation/percentage-mating-mortality",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <PercentageMatingMortalityPage />
          </Suspense>,
          [PERMISSIONS.REPORT_BREEDING_MATURATION_MATING_TOTAL_AND_MORTALITY],
        ),
      },
      {
        path: "reports/maturation/daily-feeding",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <DailyFeedingPage />
          </Suspense>,
          [PERMISSIONS.REPORT_BREEDING_MATURATION_FEEDING_DAILY],
        ),
      },
      {
        path: "reports/maturation/mating-tank",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <MatingPerTankBreakdownPage />
          </Suspense>,
          [PERMISSIONS.REPORT_BREEDING_MATURATION_MATING_PER_TANK],
        ),
      },
      {
        path: "reports/breeding-maturation/total-and-average-mating-per-tank",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <TotalAndAverageMatingPerTankPage />
          </Suspense>,
          [PERMISSIONS.REPORT_BREEDING_MATURATION_TOTAL_AVG_MATING],
        ),
      },
      {
        path: "reports/breeding-hatching/avg-egg-and-nauplii",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <AvgEggAndNaupliiPage />
          </Suspense>,
          [PERMISSIONS.REPORT_BREEDING_HATCHING_AVG_EGG_NAUPLI],
        ),
      },
      {
        path: "reports/breeding-hatching/avg-fertil-and-hatching",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <AvgFertilAndHatchingPage />
          </Suspense>,
          [PERMISSIONS.REPORT_BREEDING_HATCHING_AVG_FERTIL_HATCHING_PERCENTAGE],
        ),
      },
      {
        path: "reports/breeding-larvae/avg-pl10-population",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <AvgPL10PopulationPage />
          </Suspense>,
          [PERMISSIONS.REPORT_BREEDING_LARVAE_AVG_PL10_POPULATION],
        ),
      },
      {
        path: "reports/breeding-larvae/tank-temperature",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <LarvaeTankTemperaturePage />
          </Suspense>,
          [PERMISSIONS.REPORT_BREEDING_LARVAE_LARVAE_TEMPERATURE],
        ),
      },
      {
        path: "reports/breeding-growout/avg-weekly-grow-rate-batch",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <AvgWeeklyGrowRateBatchPage />
          </Suspense>,
          [PERMISSIONS.REPORT_BREEDING_GROWOUT_AVG_WEIGHT],
        ),
      },
      {
        path: "reports/breeding-growout/avg-daily-gain-per-batch",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <AvgDailyGainBatchPage />
          </Suspense>,
          [PERMISSIONS.REPORT_BREEDING_GROWOUT_AVG_ADG],
        ),
      },
      {
        path: "reports/breeding-larvae/pl4-pl10-population-and-sr",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <PL4PL10PopulationPage />
          </Suspense>,
          [PERMISSIONS.REPORT_BREEDING_LARVAE_POPULATION_AND_SR_PL4_ESTIMATE],
        ),
      },
      {
        path: "reports/breeding-larvae/pl-trial-batch",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <PLTrialBatchPage />
          </Suspense>,
          [PERMISSIONS.REPORT_BREEDING_MATURATION_PL_TRIAL_BATCH],
        ),
      },
      {
        path: "reports/pond-trial/weight-sr",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <ReportWeightSRPage />
          </Suspense>,
          [PERMISSIONS.BREEDPROGRAM_VIEW],
        ),
      },
      {
        path: "reports/cage-trial/weight-sr",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <CageTrialReportWeightSRPage />
          </Suspense>,
          [PERMISSIONS.REPORT_CAGETRIAL_WEIGHT_SR],
        ),
      },
      {
        path: "reports/cage-trial/cage-deformities",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <CageTrialReportCageDeformitiesPage />
          </Suspense>,
          [PERMISSIONS.REPORT_CAGETRIAL_CAGE_DEFORMITIES],
        ),
      },
      {
        path: "reports/cage-trial/all-cages-deformities",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <CageTrialReportAllCagesDeformitiesPage />
          </Suspense>,
          [PERMISSIONS.REPORT_CAGETRIAL_ALL_CAGE_DEFORMITIES],
        ),
      },
      {
        path: "reports/cage-trial/nbc-deformities",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <CageTrialReportNbcDeformitiesPage />
          </Suspense>,
          [PERMISSIONS.REPORT_CAGETRIAL_NBC_DEFORMITIES],
        ),
      },
      {
        path: "reports/cage-trial/tagging-shipping",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <CageTrialReportTaggingShippingPage />
          </Suspense>,
          [PERMISSIONS.REPORT_CAGETRIAL_TAGGING_SHIPPING],
        ),
      },
      {
        path: "reports/pl-trial/sample-submission",
        element: (
          <Suspense fallback={<PageLoader />}>
            <PLTrialReportSampleSubmissionPage />
          </Suspense>
        ),
      },
      {
        path: "reports/breeding-growout/sample-submission",
        element: (
          <Suspense fallback={<PageLoader />}>
            <GrowoutReport20GrSampleSubmissionPage />
          </Suspense>
        ),
      },
      {
        path: "reports/cage-trial/sample-submission",
        element: (
          <Suspense fallback={<PageLoader />}>
            <CageTrialReportSampleSubmissionPage />
          </Suspense>
        ),
      },
      {
        path: "reports/pond-trial/cage-deformities",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <ReportCageDeformitiesPage />
          </Suspense>,
          [PERMISSIONS.BREEDPROGRAM_VIEW],
        ),
      },
      {
        path: "reports/pond-trial/all-cages-deformities",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <ReportAllCagesDeformitiesPage />
          </Suspense>,
          [PERMISSIONS.BREEDPROGRAM_VIEW],
        ),
      },
      {
        path: "reports/pond-trial/nbc-deformities",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <ReportNbcDeformitiesPage />
          </Suspense>,
          [PERMISSIONS.BREEDPROGRAM_VIEW],
        ),
      },
      {
        path: "reports/pond-trial/tagging-shipping",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <ReportTaggingShippingPage />
          </Suspense>,
          [PERMISSIONS.BREEDPROGRAM_VIEW],
        ),
      },
      {
        path: "reports/disease-challenge/weight-sr",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <DiseaseChallengeReportWeightSRPage />
          </Suspense>,
          [PERMISSIONS.REPORT_DISEASE_CHALLENGE_WEIGHT_SR],
        ),
      },
      {
        path: "reports/disease-challenge/cage-deformities",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <DiseaseChallengeReportCageDeformitiesPage />
          </Suspense>,
          [PERMISSIONS.REPORT_DISEASE_CHALLENGE_CAGE_DEFORMITIES],
        ),
      },
      {
        path: "reports/disease-challenge/all-cages-deformities",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <DiseaseChallengeReportAllCagesDeformitiesPage />
          </Suspense>,
          [PERMISSIONS.REPORT_DISEASE_CHALLENGE_ALL_CAGE_DEFORMITIES],
        ),
      },
      {
        path: "reports/disease-challenge/sample-submission",
        element: (
          <Suspense fallback={<PageLoader />}>
            <DiseaseChallengeReportSampleSubmissionPage />
          </Suspense>
        ),
      },
      {
        path: "reports",
        element: (
          <PageWithErrorBoundary>
            <PlaceholderPage title="Reports" />
          </PageWithErrorBoundary>
        ),
      },
      {
        path: "my-tasks",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <MyTasksPage />
          </Suspense>,
          [PERMISSIONS.MYTASK_VIEW],
        ),
      },
      {
        path: "my-tasks/:taskId",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <TaskDetailPage />
          </Suspense>,
          [PERMISSIONS.MYTASK_VIEW],
        ),
      },
      {
        path: "tasks/schedule-template",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <ScheduleTemplatePage />
          </Suspense>,
          [PERMISSIONS.SCHEDULETEMPLATE_VIEW],
        ),
      },
      {
        path: "tasks/schedule-template/new",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <ScheduleTemplateDetail />
          </Suspense>,
          [PERMISSIONS.SCHEDULETEMPLATE_VIEW],
        ),
      },
      {
        path: "tasks/redelegate",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <RedelegateTasksPage />
          </Suspense>,
          [PERMISSIONS.REDELEGATETASK_VIEW],
        ),
      },
      {
        path: "tasks/redelegate/add",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <AddRedelegateTaskPage />
          </Suspense>,
          [PERMISSIONS.REDELEGATETASK_VIEW],
        ),
      },
      {
        path: "tasks/schedule-template/:id",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <ScheduleTemplateDetail />
          </Suspense>,
          [PERMISSIONS.SCHEDULETEMPLATE_VIEW],
        ),
      },
      {
        path: "tasks/schedule-group-tasks",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <ScheduleGroupTasksPage />
          </Suspense>,
          [PERMISSIONS.SCHEDULETASK_VIEW],
        ),
      },
      {
        path: "tasks/schedule-group-tasks/new",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <ScheduleGroupTaskDetail />
          </Suspense>,
          [PERMISSIONS.SCHEDULETASK_VIEW],
        ),
      },
      {
        path: "tasks/schedule-group-tasks/:id",
        element: withGuard(
          <Suspense fallback={<PageLoader />}>
            <ScheduleGroupTaskDetail />
          </Suspense>,
          [PERMISSIONS.SCHEDULETASK_VIEW],
        ),
      },
    ],
    errorElement: <RouteErrorElement />,
  },
]);

export const AppRouter = () => {
  return <RouterProvider router={router} />;
};
